import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip
} from '@material-ui/core';
import Button from '../widgets/button';
import { toCurrency, getCookieValue } from '../../utils/utils';

// ── Constants ─────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 60000;

const PHASE = {
    IDLE:      'idle',
    UPLOADING: 'uploading',
    POLLING:   'polling',
    RESULTS:   'results',
    CACHED:    'cached',
    ERROR:     'error'
};

// ── Item normalisation (same logic as applyImportedValues in progressClaims.jsx)
// Trailing decimal zeros are intentionally NOT stripped: "1102.10" and "1102.1"
// are treated as distinct codes because they may be separate schedule entries.
const normalizeItemNo = (raw) => {
    if (!raw) return '';
    return raw.replace(/\s+/g, '').toLowerCase().replace(/:$/, '');
};

// ── Discrepancy check ─────────────────────────────────────────────────────────
// Returns true if either the amount OR the quantity differs significantly from
// the current schedule value. Flags both so the user sees all anomalies before
// applying — not just monetary differences.
const needsReview = (extracted, schedule) => {
    // Amount check: absolute diff > $1 AND relative diff > 1%
    const amtDiff = Math.abs((extracted.claimAmount || 0) - (schedule.claimAmount || 0));
    if (amtDiff > 1) {
        const amtBase = Math.max(Math.abs(schedule.claimAmount || 0), 1);
        if (amtDiff / amtBase > 0.01) return true;
    }
    // Qty check: relative diff > 1% (only when both sides have a meaningful qty)
    if (extracted.claimQty != null && schedule.claimQty != null && schedule.claimQty !== 0) {
        const qtyDiff = Math.abs(extracted.claimQty - schedule.claimQty);
        if (qtyDiff / Math.abs(schedule.claimQty) > 0.01) return true;
    }
    return false;
};

// ── Comparison logic ──────────────────────────────────────────────────────────
const buildComparisonSections = (extractedItems, scheduleItems) => {
    // Build lookup: normalized itemNo → schedule item.
    // Exclude Temporary claim items — they are contractor-added and won't match.
    const scheduleMap = {};
    (scheduleItems || [])
        .filter(si => si.claimType !== 'Temporary' && si.itemNo)
        .forEach(si => {
            const key = normalizeItemNo(si.itemNo);
            if (key) scheduleMap[key] = si;
        });

    const matched       = [];
    const review        = [];
    const extractedOnly = [];
    const usedKeys      = new Set();

    (extractedItems || []).forEach(ei => {
        const key = normalizeItemNo(ei.itemNo);
        const si  = scheduleMap[key];
        if (!si) {
            extractedOnly.push(ei);
            return;
        }
        usedKeys.add(key);
        if (needsReview(ei, si)) {
            review.push({ extracted: ei, schedule: si });
        } else {
            matched.push({ extracted: ei, schedule: si });
        }
    });

    const scheduleOnly = (scheduleItems || [])
        .filter(si => si.claimType !== 'Temporary' && si.itemNo)
        .filter(si => !usedKeys.has(normalizeItemNo(si.itemNo)));

    const confidence = extractedItems && extractedItems.length > 0
        ? Math.round(matched.length / extractedItems.length * 100)
        : 0;

    return { matched, review, extractedOnly, scheduleOnly, confidence };
};

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ label, count, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 8px' }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginRight: 8 }}>
            {label}
        </Typography>
        <Chip
            label={count}
            size="small"
            style={{ backgroundColor: color, color: '#fff', height: 20, fontSize: 12 }}
        />
    </div>
);

SectionHeader.propTypes = {
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired
};

// ── Main component ────────────────────────────────────────────────────────────
const ImportProgressClaimDialog = ({ show, onClose, onApply, contractId, periodId, scheduleItems }) => {
    const [phase,        setPhase]       = useState(PHASE.IDLE);
    const [selectedFile, setSelectedFile] = useState(null);
    const [errorMsg,     setErrorMsg]    = useState('');
    const [cachedMsg,    setCachedMsg]   = useState('');
    const [sections,     setSections]    = useState(null);
    const [warnings,     setWarnings]    = useState([]);
    const [stats,        setStats]       = useState(null);

    const fileInputRef    = useRef(null);
    const pollTimerRef    = useRef(null);
    const timeoutTimerRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current)    clearInterval(pollTimerRef.current);
        if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
        pollTimerRef.current    = null;
        timeoutTimerRef.current = null;
    }, []);

    const handleClose = useCallback(() => {
        stopPolling();
        setPhase(PHASE.IDLE);
        setSelectedFile(null);
        setErrorMsg('');
        setCachedMsg('');
        setSections(null);
        setWarnings([]);
        setStats(null);
        onClose();
    }, [onClose, stopPolling]);

    const handleFileChange = (e) => {
        const f = e.target.files && e.target.files[0];
        setSelectedFile(f || null);
        setErrorMsg('');
    };

    const processResult = useCallback((result) => {
        if (!result.success) {
            setErrorMsg(result.message || 'Extraction failed.');
            setPhase(PHASE.ERROR);
            return;
        }
        const s = buildComparisonSections(result.items, scheduleItems);
        setSections(s);
        const baseWarnings = result.warnings || [];
        setWarnings(result.isCached
            ? ['Cached result — no AI call was made. You can still apply these items.', ...baseWarnings]
            : baseWarnings
        );
        setStats({
            inputTokens:  result.inputTokens,
            outputTokens: result.outputTokens,
            costUsd:      result.estimatedCostUsd,
            itemCount:    (result.items || []).length
        });
        setPhase(PHASE.RESULTS);
    }, [scheduleItems]);

    const startPolling = useCallback((jobId) => {
        setPhase(PHASE.POLLING);

        timeoutTimerRef.current = setTimeout(() => {
            stopPolling();
            setErrorMsg('Import timed out. Please try again.');
            setPhase(PHASE.ERROR);
        }, POLL_TIMEOUT_MS);

        const csrfToken = getCookieValue('XSRF-REQUEST-TOKEN');
        const headers   = { 'X-XSRF-TOKEN': csrfToken };

        pollTimerRef.current = setInterval(async () => {
            try {
                const r = await axios.get(
                    `/api/progress-claim/parse-pdf/${jobId}?contractId=${contractId}&periodId=${periodId}`,
                    { withCredentials: true, headers }
                );
                if (r.data.complete) {
                    stopPolling();
                    processResult(r.data.result);
                }
            } catch (err) {
                stopPolling();
                const msg = (err.response && err.response.data && err.response.data.error)
                    || err.message || 'Polling failed.';
                setErrorMsg(msg);
                setPhase(PHASE.ERROR);
            }
        }, POLL_INTERVAL_MS);
    }, [contractId, periodId, processResult, stopPolling]);

    const handleUpload = useCallback(async () => {
        if (!selectedFile) return;
        setPhase(PHASE.UPLOADING);
        setErrorMsg('');

        const csrfToken = getCookieValue('XSRF-REQUEST-TOKEN');
        const formData  = new FormData();
        formData.append('file', selectedFile);

        try {
            const r = await axios.post(
                `/api/progress-claim/parse-pdf?contractId=${contractId}&periodId=${periodId}`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'X-XSRF-TOKEN': csrfToken
                    }
                }
            );
            startPolling(r.data.jobId);
        } catch (err) {
            const msg = (err.response && err.response.data && err.response.data.error)
                || err.message || 'Upload failed.';
            setErrorMsg(msg);
            setPhase(PHASE.ERROR);
        }
    }, [selectedFile, contractId, periodId, startPolling]);

    // mode: 'matched' applies only exact matches; 'all' includes needs-review items too
    const handleApply = useCallback((mode) => {
        if (!sections) return;
        const toApply = [
            ...sections.matched.map(r => ({
                itemNo:      r.extracted.itemNo,
                claimQty:    r.extracted.claimQty,
                claimAmount: r.extracted.claimAmount
            })),
            ...(mode === 'all' ? sections.review.map(r => ({
                itemNo:      r.extracted.itemNo,
                claimQty:    r.extracted.claimQty,
                claimAmount: r.extracted.claimAmount
            })) : [])
        ];
        onApply(toApply);
        handleClose();
    }, [sections, onApply, handleClose]);

    // ── Render ────────────────────────────────────────────────────────────────

    const renderIdle = () => (
        <div>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
                Upload the contractor&apos;s PDF progress claim. The AI will extract line items
                and compare them to the current schedule.
            </Typography>
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <Button
                label={selectedFile ? selectedFile.name : 'Choose PDF…'}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                primary={false}
            />
            {errorMsg && (
                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                    {errorMsg}
                </Typography>
            )}
        </div>
    );

    const renderSpinner = (msg) => (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" style={{ marginTop: 12 }}>{msg}</Typography>
        </div>
    );

    const renderError = () => (
        <Typography variant="body2" color="error">{errorMsg}</Typography>
    );

    const renderCached = () => (
        <div>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
                {cachedMsg}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                To get a fresh result, wait an hour or upload a different PDF.
            </Typography>
        </div>
    );

    const renderResults = () => {
        if (!sections) return null;
        const { matched, review, extractedOnly, scheduleOnly, confidence } = sections;
        const confidenceColor = confidence >= 80 ? '#43a047' : confidence >= 50 ? '#fb8c00' : '#e53935';

        return (
            <div style={{ minWidth: 500 }}>
                {/* Summary bar */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span>
                        <strong>Match confidence:</strong>{' '}
                        <span style={{ color: confidenceColor, fontWeight: 700 }}>{confidence}%</span>
                    </span>
                    {stats && (
                        <span style={{ color: '#888', fontSize: 12 }}>
                            {stats.itemCount} items &middot; {stats.inputTokens + stats.outputTokens} tokens &middot;
                            ~${stats.costUsd != null ? Number(stats.costUsd).toFixed(4) : '0.0000'} USD
                        </span>
                    )}
                </div>

                {/* Warnings */}
                {warnings.map((w, i) => (
                    <Typography key={i} variant="body2" style={{ color: '#fb8c00', marginBottom: 4 }}>
                        ⚠ {w}
                    </Typography>
                ))}

                {/* Matched */}
                {matched.length > 0 && (
                    <>
                        <SectionHeader label="Matched" count={matched.length} color="#43a047" />
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Extracted Qty</TableCell>
                                    <TableCell align="right">Extracted Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matched.map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{r.extracted.itemNo}</TableCell>
                                        <TableCell align="right">{r.extracted.claimQty != null ? r.extracted.claimQty : '—'}</TableCell>
                                        <TableCell align="right">
                                            {r.extracted.claimAmount != null ? toCurrency(r.extracted.claimAmount) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}

                {/* Needs Review */}
                {review.length > 0 && (
                    <>
                        <SectionHeader label="Needs Review" count={review.length} color="#fb8c00" />
                        <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 4 }}>
                            Extracted amount differs from the current schedule amount by more than 1%.
                            Check before applying.
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Schedule Amount</TableCell>
                                    <TableCell align="right">Extracted Amount</TableCell>
                                    <TableCell align="right">Difference</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {review.map((r, i) => {
                                    const diff = (r.extracted.claimAmount || 0) - (r.schedule.claimAmount || 0);
                                    return (
                                        <TableRow key={i}>
                                            <TableCell>{r.extracted.itemNo}</TableCell>
                                            <TableCell align="right">
                                                {r.schedule.claimAmount != null ? toCurrency(r.schedule.claimAmount) : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.extracted.claimAmount != null ? toCurrency(r.extracted.claimAmount) : '—'}
                                            </TableCell>
                                            <TableCell align="right" style={{ color: diff > 0 ? '#43a047' : '#e53935' }}>
                                                {diff >= 0 ? '+' : ''}{toCurrency(diff)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}

                {/* Extracted Only */}
                {extractedOnly.length > 0 && (
                    <>
                        <SectionHeader label="In PDF only (not applied)" count={extractedOnly.length} color="#9e9e9e" />
                        <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 4 }}>
                            Found in the PDF but no matching schedule entry — these will not be applied.
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Extracted Qty</TableCell>
                                    <TableCell align="right">Extracted Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {extractedOnly.map((ei, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{ei.itemNo}</TableCell>
                                        <TableCell align="right">{ei.claimQty != null ? ei.claimQty : '—'}</TableCell>
                                        <TableCell align="right">
                                            {ei.claimAmount != null ? toCurrency(ei.claimAmount) : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}

                {/* Schedule Only */}
                {scheduleOnly.length > 0 && (
                    <>
                        <SectionHeader label="In schedule only (unchanged)" count={scheduleOnly.length} color="#bdbdbd" />
                        <Typography variant="caption" style={{ color: '#888', display: 'block' }}>
                            These schedule items were not found in the PDF and will remain unchanged.
                        </Typography>
                    </>
                )}
            </div>
        );
    };

    const renderContent = () => {
        switch (phase) {
            case PHASE.IDLE:      return renderIdle();
            case PHASE.UPLOADING: return renderSpinner('Uploading…');
            case PHASE.POLLING:   return renderSpinner('Extracting line items from PDF…');
            case PHASE.ERROR:     return renderError();
            case PHASE.CACHED:    return renderCached();
            case PHASE.RESULTS:   return renderResults();
            default:              return null;
        }
    };

    const renderActions = () => {
        const matchedCount = sections ? sections.matched.length : 0;
        const allCount     = sections ? sections.matched.length + sections.review.length : 0;

        switch (phase) {
            case PHASE.IDLE:
                return (
                    <>
                        <Button label="Cancel" onClick={handleClose} />
                        <Button
                            label="Extract Items"
                            primary={true}
                            disabled={!selectedFile}
                            onClick={handleUpload}
                        />
                    </>
                );
            case PHASE.UPLOADING:
            case PHASE.POLLING:
                return <Button label="Cancel" onClick={handleClose} />;
            case PHASE.CACHED:
                return <Button label="Close" onClick={handleClose} />;
            case PHASE.ERROR:
                return (
                    <>
                        <Button label="Close" onClick={handleClose} />
                        <Button label="Try Again" primary={true} onClick={() => {
                            setPhase(PHASE.IDLE);
                            setErrorMsg('');
                        }} />
                    </>
                );
            case PHASE.RESULTS:
                return (
                    <>
                        <Button label="Cancel" onClick={handleClose} />
                        {sections && sections.review.length > 0 && (
                            <Button
                                label={`Apply Matched Only (${matchedCount})`}
                                disabled={matchedCount === 0}
                                onClick={() => handleApply('matched')}
                            />
                        )}
                        <Button
                            label={sections && sections.review.length > 0
                                ? `Apply All incl. Review (${allCount})`
                                : `Apply ${allCount} Items`}
                            primary={true}
                            disabled={allCount === 0}
                            onClick={() => handleApply('all')}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const titleMap = {
        [PHASE.IDLE]:      'Import PDF Progress Claim',
        [PHASE.UPLOADING]: 'Import PDF Progress Claim',
        [PHASE.POLLING]:   'Extracting…',
        [PHASE.ERROR]:     'Import Failed',
        [PHASE.CACHED]:    'Already Processed',
        [PHASE.RESULTS]:   'Extraction Results'
    };

    return (
        <Dialog
            open={show}
            onClose={(_e, reason) => { if (reason !== 'backdropClick') handleClose(); }}
            maxWidth="md"
            fullWidth={phase === PHASE.RESULTS}
        >
            <DialogTitle>{titleMap[phase]}</DialogTitle>
            <DialogContent style={{ paddingBottom: 8 }}>
                {renderContent()}
            </DialogContent>
            <DialogActions style={{ padding: '8px 24px 16px' }}>
                {renderActions()}
            </DialogActions>
        </Dialog>
    );
};

ImportProgressClaimDialog.propTypes = {
    show:          PropTypes.bool.isRequired,
    onClose:       PropTypes.func.isRequired,
    onApply:       PropTypes.func.isRequired,
    contractId:    PropTypes.number.isRequired,
    periodId:      PropTypes.number.isRequired,
    scheduleItems: PropTypes.array
};

ImportProgressClaimDialog.defaultProps = {
    scheduleItems: []
};

export default ImportProgressClaimDialog;
